import type { ExperimentDetail } from "../api/experimentsApi";
import type { ExperimentFormData } from "../types/experiment.types";

export const initialExperimentFormData: ExperimentFormData = {
  name: "",
  goal: "",
  description: "",
  targetAudience: "",
  supportingDocs: [],
  supportingDocFiles: [],
};

export const serializeContextStep = (data: ExperimentFormData): string =>
  JSON.stringify({
    name: data?.name ?? "",
    goal: data?.goal ?? "",
    description: data?.description ?? "",
    targetAudience: data?.targetAudience ?? "",
    supportingDocs: (data?.supportingDocs ?? []).map((doc) => ({
      id: doc.id ?? "",
      filename: doc.filename ?? doc.name ?? "",
      url: doc.url ?? "",
    })),
    supportingDocFiles: (data?.supportingDocFiles ?? []).map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
    })),
  });

export const serializeResearchStep = (data: ExperimentFormData): string =>
  JSON.stringify({
    experimentType: data?.experimentType ?? "",
    researchObjective: data?.researchObjective ?? "",
    coreResearchQuestions: data?.coreResearchQuestions ?? [],
    primaryHypothesis: data?.primaryHypothesis ?? "",
    keyAssumptions: data?.keyAssumptions ?? [],
    successSignals: data?.successSignals ?? {
      qualitative: "",
      quantitative: "",
    },
    okrs: data?.okrs ?? [],
    attachments: data?.attachments ?? [],
  });

export const mapExperimentDetailToFormData = (
  experiment: ExperimentDetail,
): ExperimentFormData => {
  const coreResearchQuestions = (experiment.premises ?? [])
    .filter((premise) => premise.type === 1)
    .map((premise) => premise.value);
  const keyAssumptions = (experiment.premises ?? [])
    .filter((premise) => premise.type === 2)
    .map((premise) => premise.value);
  const successSignals = (experiment.premises ?? [])
    .filter((premise) => premise.type === 3)
    .map((premise) => premise.value);

  return {
    ...initialExperimentFormData,
    experimentId: experiment.id,
    name: experiment.name || initialExperimentFormData.name,
    goal: experiment.goal || initialExperimentFormData.goal,
    description: experiment.context || initialExperimentFormData.description,
    targetAudience:
      experiment.audience || initialExperimentFormData.targetAudience,
    owner: experiment.owner || initialExperimentFormData.owner,
    experimentType:
      experiment.type === 1
        ? "discovery"
        : experiment.type === 3
          ? "optimization"
          : "validation",
    researchObjective:
      experiment.foundation?.objective ??
      initialExperimentFormData.researchObjective,
    primaryHypothesis:
      experiment.foundation?.hypothesis ??
      initialExperimentFormData.primaryHypothesis,
    coreResearchQuestions,
    keyAssumptions,
    successSignals: {
      qualitative: successSignals[0] || "",
      quantitative: successSignals[1] || "",
    },
    okrs: experiment.okrs,
    attachments: experiment.attachments,
    supportingDocs: experiment.attachments,
  };
};
